'use client';

import { createContext, useContext, useReducer, useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { detectTier } from '@/lib/deviceTier';

// ─── Sound file mappings ────────────────────────────────────────────────────

const MUSIC_PLAYLISTS = {
  menu: [
    '/assets/sounds/music/mein-menu-2.mp3',
    '/assets/sounds/music/mein-menu-3.mp3',
    '/assets/sounds/music/mein-menu-4.mp3',
  ],
  game: [
    '/assets/sounds/music/game-board-1.mp3',
    '/assets/sounds/music/game-board-2.mp3',
    '/assets/sounds/music/game-board-3.mp3',
    '/assets/sounds/music/game-board-4.mp3',
  ],
};

// Sound effects with variant support
const SFX_MAP = {
  'zagranie-karty': [
    '/assets/sounds/soundeffects/zagranie-karty-1.mp3',
    '/assets/sounds/soundeffects/zagranie-karty-2.mp3',
    '/assets/sounds/soundeffects/zagranie-karty-3.mp3',
  ],
  'kupno-karty': ['/assets/sounds/soundeffects/kupno-karty.mp3'],
  'dociag-kart': ['/assets/sounds/soundeffects/dociag-kart.mp3'],
  'klikniecie-przycisku-ui': ['/assets/sounds/soundeffects/klikniecie-przycisku-ui.mp3'],
  'atak-potwora': [
    '/assets/sounds/soundeffects/atak-potwora-1.mp3',
    '/assets/sounds/soundeffects/atak-potwora-2.mp3',
    '/assets/sounds/soundeffects/atak-potwora-3.mp3',
  ],
  'pojawienie-sie-bosa-na-sciezce-mestwa': ['/assets/sounds/soundeffects/pojawienie-sie-bosa-na-sciezce-mestwa.mp3'],
  'pojawienie-sie-epickiego-zadania-pulapki': ['/assets/sounds/soundeffects/pojawienie-sie-epickiego-zadania-pulapki.mp3'],
  'pokonanie-pulapki': ['/assets/sounds/soundeffects/pokonanie-pulapki.mp3'],
  'ukonczenie-epickiego-zadania-pulapki': ['/assets/sounds/soundeffects/ukonczenie-epickiego-zadania-pulapki.mp3'],
  'ukonczenie-kontraktu-gildii': ['/assets/sounds/soundeffects/ukonczenie-kontraktu-gildii.mp3'],
  'ukonczenie-zadania': ['/assets/sounds/soundeffects/ukonczenie-zadania.mp3'],
  // Variants - random pick from 3
  'otrzymanie-zlota': [
    '/assets/sounds/soundeffects/otrzymanie-zlota-1.mp3',
    '/assets/sounds/soundeffects/otrzymanie-zlota-2.mp3',
    '/assets/sounds/soundeffects/otrzymanie-zlota-3.mp3',
  ],
  'otrzymanie-wiedzy': [
    '/assets/sounds/soundeffects/otrzymanie-wiedzy-1.mp3',
    '/assets/sounds/soundeffects/otrzymanie-wiedzy-2.mp3',
    '/assets/sounds/soundeffects/otrzymanie-wiedzy-3.mp3',
  ],
  'otrzymanie-sily': [
    '/assets/sounds/soundeffects/otrzymanie-sily-1.mp3',
    '/assets/sounds/soundeffects/otrzymanie-sily-2.mp3',
    '/assets/sounds/soundeffects/otrzymanie-sily-3.mp3',
  ],
  'wejscie-do-miasta': ['/assets/sounds/soundeffects/city_visit_1.mp3'],
  'marsz-wojsk': ['/assets/sounds/soundeffects/marsz-wojsk-1.mp3'],
  'wisdom-move': ['/assets/sounds/soundeffects/wisdom-move-1.mp3'],
};

// ─── State management ───────────────────────────────────────────────────────

const STORAGE_KEY = 'kroniki-sound-settings';

const defaultSettings = {
  musicEnabled: true,
  sfxEnabled: true,
  musicVolume: 0.15,
  sfxVolume: 0.5,
  parallaxEnabled: false,
  canvasZoomEnabled: false,
  handHoverZoomEnabled: true,
  pathHoverZoomEnabled: true,
  animationsEnabled: true,
  // Szybkie zagrywanie kart (domyślne): klik gra od razu bez potwierdzania,
  // hover pokazuje kartę na środku ekranu. Wyłączenie przywraca tryb
  // podnieś-i-potwierdź (scena z przyciskiem Zagraj).
  instantPlayEnabled: true,
};

function loadSettings() {
  if (typeof window === 'undefined') return defaultSettings;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...defaultSettings, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return defaultSettings;
}

function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch { /* ignore */ }
}

function soundReducer(state, action) {
  switch (action.type) {
    case 'INIT_SETTINGS':
      return { ...state, ...action.payload };
    case 'TOGGLE_MUSIC': {
      const next = { ...state, musicEnabled: !state.musicEnabled };
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_SFX': {
      const next = { ...state, sfxEnabled: !state.sfxEnabled };
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_ALL': {
      const allOff = !state.musicEnabled && !state.sfxEnabled;
      const next = { ...state, musicEnabled: allOff, sfxEnabled: allOff };
      saveSettings(next);
      return next;
    }
    case 'SET_MUSIC_VOLUME': {
      const next = { ...state, musicVolume: action.payload };
      saveSettings(next);
      return next;
    }
    case 'SET_SFX_VOLUME': {
      const next = { ...state, sfxVolume: action.payload };
      console.log('SFX', next);
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_PARALLAX': {
      const next = { ...state, parallaxEnabled: !state.parallaxEnabled };
      saveSettings(next);
      return next;
    }
    case 'SET_PARALLAX': {
      const next = { ...state, parallaxEnabled: action.payload };
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_CANVAS_ZOOM': {
      const next = { ...state, canvasZoomEnabled: !state.canvasZoomEnabled };
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_HAND_HOVER_ZOOM': {
      const next = { ...state, handHoverZoomEnabled: !state.handHoverZoomEnabled };
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_INSTANT_PLAY': {
      const next = { ...state, instantPlayEnabled: !state.instantPlayEnabled };
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_PATH_HOVER_ZOOM': {
      const next = { ...state, pathHoverZoomEnabled: !state.pathHoverZoomEnabled };
      saveSettings(next);
      return next;
    }
    case 'TOGGLE_ANIMATIONS': {
      const next = { ...state, animationsEnabled: !state.animationsEnabled };
      saveSettings(next);
      return next;
    }
    default:
      return state;
  }
}

// ─── Context ────────────────────────────────────────────────────────────────

const SoundContext = createContext(null);

// ─── Pick random helper ─────────────────────────────────────────────────────

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function SoundProvider({ children }) {
  const [state, dispatch] = useReducer(soundReducer, defaultSettings, loadSettings);

  // Tryb TV (aplikacja tv-android: UA KronikiTV, LG webOS: Web0S, albo ?tv=1): bez powiększania kart po
  // najechaniu i z dźwiękiem bez gestu. Animacje zostają jak na PC — na buildzie
  // produkcyjnym TV je uciąga (w trybie dev były wyłączane, teraz już nie).
  const [tvLite, setTvLite] = useState(false);
  useEffect(() => {
    const isTv = /KronikiTV|Web0S/.test(navigator.userAgent)
      || new URLSearchParams(window.location.search).get('tv') === '1';
    if (isTv) queueMicrotask(() => setTvLite(true));
  }, []);

  // Perf tier ('low'|'high'|null) — wykrywany raz przez lib/deviceTier.js
  // (heurystyka CPU/RAM + pomiar fps), zapisywany w localStorage. Tier steruje
  // TYLKO html[data-perf] i regułami CSS ścinającymi koszty niewidoczne dla oka
  // (backdrop-filter, blur, blend, drobne cząsteczki). Animacje zostają zawsze
  // włączone — decyzja użytkownika, dają „feeling" gry.
  const [perfTier, setPerfTier] = useState(null);
  useEffect(() => {
    const isTv = /KronikiTV|Web0S/.test(navigator.userAgent)
      || new URLSearchParams(window.location.search).get('tv') === '1';
    if (isTv) {
      // TV ma własny tryb lekki w CSS (data-tv) — perf tier zostaje 'high'.
      setPerfTier('high');
      return;
    }
    detectTier().then(setPerfTier);
  }, []);

  const animationsOn = state.animationsEnabled;

  // Wyciszenie kanału pilota TV (telefon gra na TV — niepersistowane, sesyjne)
  const [audioSuppressed, setAudioSuppressed] = useState(false);
  const audioSuppressedRef = useRef(false);
  useEffect(() => { audioSuppressedRef.current = audioSuppressed; }, [audioSuppressed]);

  // Muzyka: pauza/resume bez ruszania zapisanych preferencji użytkownika
  useEffect(() => {
    const a = currentAudioRef.current;
    if (!a) return;
    if (audioSuppressed) {
      a.pause();
    } else if (state.musicEnabled && a.paused) {
      a.play().catch(() => {});
    }
  }, [audioSuppressed, state.musicEnabled]);

  // Refs for music playback
  const currentAudioRef = useRef(null);    // currently playing Audio element
  const sceneRef = useRef(null);           // 'menu' | 'game' | null
  const userInteractedRef = useRef(false);
  const fadeIntervalRef = useRef(null);

  // Ref for active SFX/Narration to allow stopping them
  const activeSoundsRef = useRef(new Map());

  // Refs for sfx settings — always current, avoid stale closures in playSound
  const sfxEnabledRef = useRef(state.sfxEnabled);
  const sfxVolumeRef = useRef(state.sfxVolume);
  useEffect(() => { sfxEnabledRef.current = state.sfxEnabled; }, [state.sfxEnabled]);
  useEffect(() => { sfxVolumeRef.current = state.sfxVolume; }, [state.sfxVolume]);

  // Settings loaded synchronously via useReducer initializer — no async dispatch needed

  // ── Autoplay policy: listen for first user interaction ──
  useEffect(() => {
    const handleInteraction = () => {
      if (!userInteractedRef.current) {
        userInteractedRef.current = true;
        // Try to start music if a scene is set
        if (sceneRef.current) {
          startPlaylist(sceneRef.current);
        }
      }
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });
    document.addEventListener('keydown', handleInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // TV: nikt nie klika w telewizor, a WebView aplikacji TV odtwarza bez gestu
  // (setMediaPlaybackRequiresUserGesture(false)) — odblokuj muzykę od razu.
  useEffect(() => {
    if (!tvLite || userInteractedRef.current) return;
    userInteractedRef.current = true;
    if (sceneRef.current) startPlaylist(sceneRef.current);
  }, [tvLite]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Music volume sync ──
  useEffect(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.volume = state.musicEnabled ? state.musicVolume : 0;
    }
  }, [state.musicVolume, state.musicEnabled]);

  // ── Stop music when disabled ──
  useEffect(() => {
    if (!state.musicEnabled) {
      stopMusic();
    } else if (sceneRef.current && userInteractedRef.current) {
      // Re-enable: resume or start
      if (currentAudioRef.current && currentAudioRef.current.paused) {
        currentAudioRef.current.volume = state.musicVolume;
        currentAudioRef.current.play().catch(() => {});
      } else if (!currentAudioRef.current) {
        startPlaylist(sceneRef.current);
      }
    }
  }, [state.musicEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Music helpers ──
  const stopMusic = useCallback(() => {
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }
  }, []);

  // Pick one random track from the scene playlist and loop it
  const startPlaylist = useCallback((scene) => {
    const tracks = MUSIC_PLAYLISTS[scene];
    if (!tracks || !tracks.length) return;
    if (!state.musicEnabled) return;
    if (audioSuppressedRef.current) return;

    const src = pickRandom(tracks);
    const audio = new Audio(src);
    audio.volume = state.musicVolume;
    audio.loop = true;
    audio.play().catch(() => {});

    currentAudioRef.current = audio;
  }, [state.musicEnabled, state.musicVolume]);

  // Crossfade from current track to a new random track from the new scene
  const crossfadeTo = useCallback((scene) => {
    if (!state.musicEnabled || !userInteractedRef.current) return;

    const oldAudio = currentAudioRef.current;
    currentAudioRef.current = null;

    const tracks = MUSIC_PLAYLISTS[scene];
    if (!tracks || !tracks.length) {
      if (oldAudio) fadeOut(oldAudio);
      return;
    }

    const src = pickRandom(tracks);
    const newAudio = new Audio(src);
    newAudio.volume = 0;
    newAudio.loop = true;
    newAudio.play().catch(() => {});
    currentAudioRef.current = newAudio;

    // Crossfade over ~1.5s
    const targetVolume = state.musicVolume;
    const steps = 30;
    const interval = 50;
    let step = 0;

    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);

    fadeIntervalRef.current = setInterval(() => {
      step++;
      const progress = step / steps;

      if (newAudio && !newAudio.paused) {
        newAudio.volume = Math.min(targetVolume * progress, 1);
      }
      if (oldAudio && !oldAudio.paused) {
        oldAudio.volume = Math.max(targetVolume * (1 - progress), 0);
      }

      if (step >= steps) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
        if (oldAudio) {
          oldAudio.pause();
          oldAudio.src = '';
        }
      }
    }, interval);
  }, [state.musicEnabled, state.musicVolume]);

  const fadeOut = useCallback((audio) => {
    if (!audio) return;
    const startVol = audio.volume;
    const steps = 20;
    const interval = 50;
    let step = 0;
    const id = setInterval(() => {
      step++;
      audio.volume = Math.max(startVol * (1 - step / steps), 0);
      if (step >= steps) {
        clearInterval(id);
        audio.pause();
        audio.src = '';
      }
    }, interval);
  }, []);

  // ── Public: set music scene ──
  const setMusicScene = useCallback((scene) => {
    if (scene === sceneRef.current) return;
    const prevScene = sceneRef.current;
    sceneRef.current = scene;

    if (!scene) {
      stopMusic();
      return;
    }

    if (!userInteractedRef.current) {
      // Will be started on first interaction
      return;
    }

    if (prevScene && currentAudioRef.current) {
      crossfadeTo(scene);
    } else {
      startPlaylist(scene);
    }
  }, [stopMusic, crossfadeTo, startPlaylist]);

  // ── Public: stop specific sound ──
  const stopSound = useCallback((soundId) => {
    const audio = activeSoundsRef.current.get(soundId);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      activeSoundsRef.current.delete(soundId);
    }
  }, []);

  // ── Public: play sound effect ──
  // Uses refs for sfxEnabled/sfxVolume so the function reference stays stable
  // and callers don't need to re-subscribe on every volume change.
  const playSound = useCallback((soundId) => {
    if (!sfxEnabledRef.current) return;
    if (audioSuppressedRef.current) return;
    if (!soundId) return;

    let src;
    const variants = SFX_MAP[soundId];
    if (variants && variants.length) {
      src = variants[Math.floor(Math.random() * variants.length)];
    } else if (soundId.startsWith('krok_')) {
      src = `/assets/sounds/narrativ/${soundId}.mp3`;
    } else {
      console.warn(`[Sound] Unknown sound ID: ${soundId}`);
      return;
    }

    stopSound(soundId);

    const audio = new Audio(src);
    // wejscie-do-miasta is naturally loud — play at 20% of user volume
    const vol = sfxVolumeRef.current;
    audio.volume = soundId === 'wejscie-do-miasta' ? Math.min(vol * 0.2, 1) : vol;

    activeSoundsRef.current.set(soundId, audio);

    audio.play().catch(err => {
      console.warn(`[Sound] Failed to play ${soundId}:`, err);
    });

    audio.addEventListener('ended', () => {
      activeSoundsRef.current.delete(soundId);
      audio.src = '';
    });
  }, [stopSound]);

  // ── Public: toggle all sound ──
  const toggleAll = useCallback(() => {
    dispatch({ type: 'TOGGLE_ALL' });
  }, []);

  const toggleMusic = useCallback(() => {
    dispatch({ type: 'TOGGLE_MUSIC' });
  }, []);

  const toggleSfx = useCallback(() => {
    dispatch({ type: 'TOGGLE_SFX' });
  }, []);

  const toggleParallax = useCallback(() => {
    dispatch({ type: 'TOGGLE_PARALLAX' });
  }, []);

  const toggleCanvasZoom = useCallback(() => {
    dispatch({ type: 'TOGGLE_CANVAS_ZOOM' });
  }, []);

  const toggleHandHoverZoom = useCallback(() => {
    dispatch({ type: 'TOGGLE_HAND_HOVER_ZOOM' });
  }, []);

  const toggleInstantPlay = useCallback(() => {
    dispatch({ type: 'TOGGLE_INSTANT_PLAY' });
  }, []);

  const togglePathHoverZoom = useCallback(() => {
    dispatch({ type: 'TOGGLE_PATH_HOVER_ZOOM' });
  }, []);

  const toggleAnimations = useCallback(() => {
    dispatch({ type: 'TOGGLE_ANIMATIONS' });
  }, []);

  // Sync animations setting to the html element so CSS rules can disable
  // all keyframes/transitions globally via [data-animations="off"], oraz tier
  // wydajności przez [data-perf="low"] (backdrop-filter, blend-mode, cząsteczki).
  // useLayoutEffect so the attribute (and the CSS rule it triggers) applies
  // BEFORE the next paint when the user toggles in-app — otherwise there is
  // one frame where animations are still running.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.animations = animationsOn ? 'on' : 'off';
    document.documentElement.dataset.perf = perfTier === 'low' ? 'low' : 'high';
  }, [animationsOn, perfTier]);

  const setParallax = useCallback((enabled) => {
    dispatch({ type: 'SET_PARALLAX', payload: enabled });
  }, []);

  const setMusicVolume = useCallback((vol) => {
    dispatch({ type: 'SET_MUSIC_VOLUME', payload: vol });
  }, []);

  const setSfxVolume = useCallback((vol) => {
    dispatch({ type: 'SET_SFX_VOLUME', payload: vol });
  }, []);

  // ── Pause/resume on tab hide/show (mobile minimize) ──
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (currentAudioRef.current && !currentAudioRef.current.paused) {
          currentAudioRef.current.pause();
        }
        activeSoundsRef.current.forEach(audio => audio.pause());
      } else {
        if (currentAudioRef.current && state.musicEnabled) {
          currentAudioRef.current.play().catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.musicEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMusic();
      // Stop all active SFX
      activeSoundsRef.current.forEach(audio => audio.pause());
      activeSoundsRef.current.clear();
    };
  }, [stopMusic]);

  const value = {
    // State
    musicEnabled: state.musicEnabled,
    sfxEnabled: state.sfxEnabled,
    musicVolume: state.musicVolume,
    sfxVolume: state.sfxVolume,
    parallaxEnabled: state.parallaxEnabled,
    canvasZoomEnabled: state.canvasZoomEnabled,
    // TV: bez powiększania kart po najechaniu (nie ma kursora, podgląd zasłaniał stół)
    handHoverZoomEnabled: state.handHoverZoomEnabled && !tvLite,
    instantPlayEnabled: state.instantPlayEnabled,
    pathHoverZoomEnabled: state.pathHoverZoomEnabled && !tvLite,
    animationsEnabled: animationsOn,
    perfTier,
    isMuted: !state.musicEnabled && !state.sfxEnabled,
    audioSuppressed,
    // Actions
    setAudioSuppressed,
    playSound,
    stopSound, // Exported now
    setMusicScene,
    toggleAll,
    toggleMusic,
    toggleSfx,
    toggleParallax,
    setParallax,
    toggleCanvasZoom,
    toggleHandHoverZoom,
    toggleInstantPlay,
    togglePathHoverZoom,
    toggleAnimations,
    setMusicVolume,
    setSfxVolume,
  };

  return (
    <SoundContext.Provider value={value}>
      <MotionConfig
        reducedMotion={animationsOn ? 'never' : 'always'}
        transition={animationsOn ? undefined : { duration: 0 }}
      >
        {children}
      </MotionConfig>
    </SoundContext.Provider>
  );
}

export function useSoundContext() {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSoundContext must be used within a SoundProvider');
  }
  return context;
}

export default SoundContext;
